import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-background-200 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 5);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 免费韩国漫画在线观看`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2025-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
    },
  };

  return (
    <>
      <PageSEO
        title={`${comic.title}_${comic.author}_免费韩国漫画_下拉式在线观看 - 韩漫网`}
        description={comic.description}
        keywords={`韩漫网,韩国漫画,${comic.title},${comic.author},${comic.tags.join(',')},免费韩漫`}
        canonicalUrl={`/comic/${comic.id}`}
        ogType="article"
        schemaJson={pageSchema}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[380px] md:h-[500px] flex flex-col items-center justify-center overflow-hidden">
          <img
            src={comic.banner}
            alt={comic.title}
            title={`${comic.title} - 免费韩国漫画`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50" />

          <div className="relative z-10 w-full flex flex-col items-center text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-2">
              {comic.title}
            </h1>
            <p className="text-lg text-background-50/80 mb-3">
              {comic.author} · {comic.status}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-50/15 backdrop-blur-sm text-background-50 text-xs rounded-full whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <button className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
              开始阅读
              <i className="ri-play-line w-4 h-4 flex items-center justify-center" />
            </button>
          </div>
        </section>

        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category}漫画`, href: `/category/${comic.category === '热血' ? 'rexue' : comic.category === '恋爱' ? 'lianai' : comic.category === '悬疑' ? 'xuanyi' : comic.category === '奇幻' ? 'qihuan' : comic.category === '校园' ? 'xiaoyuan' : comic.category === '科幻' ? 'kehuan' : comic.category === '恐怖' ? 'kongbu' : 'gaoxiao'}` },
              { label: comic.title },
            ]}
          />
        </div>

        {/* Detail & Review Section */}
        <section className="py-10 md:py-16 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-[40%]">
                <div className="rounded-3xl overflow-hidden bg-accent-100 aspect-[3/4] max-w-sm mx-auto lg:max-w-none">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - ${comic.author} - 韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:w-[60%] flex flex-col justify-center">
                <span className="text-xs text-foreground-500 font-medium tracking-wide mb-4">
                  （用户口碑）
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-1">
                  全网热议
                </h2>
                <p className="font-heading text-3xl md:text-4xl font-bold text-secondary-400 mb-10">
                  评分 {comic.rating}
                </p>

                <blockquote className="mb-8">
                  <p className="text-base md:text-lg text-foreground-700 leading-relaxed before:content-['\201C'] after:content-['\201D']">
                    {comic.description}
                  </p>
                </blockquote>
                <p className="text-sm font-semibold text-foreground-800 mb-10">
                  - {comic.author}
                </p>

                <div className="flex items-center gap-4">
                  <button className="w-12 h-12 rounded-full border border-foreground-300 flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-all cursor-pointer" aria-label="上一章">
                    <i className="ri-arrow-left-line text-lg w-5 h-5 flex items-center justify-center" />
                  </button>
                  <button className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center text-background-50 hover:bg-primary-600 transition-all cursor-pointer" aria-label="下一章">
                    <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center" />
                  </button>
                  <span className="text-sm text-foreground-500 ml-2">共 {comic.chapters} 话</span>
                </div>

                <div className="flex flex-wrap gap-4 mt-8">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500">浏览量</span>
                    <span className="font-semibold text-foreground-900">{comic.views}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500">状态</span>
                    <span className={`font-semibold ${comic.status === '连载中' ? 'text-primary-500' : 'text-foreground-500'}`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-foreground-500">最近更新</span>
                    <span className="font-semibold text-foreground-900">{comic.updatedAt}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              章节<span className="text-primary-500">列表</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {Array.from({ length: Math.min(20, comic.chapters) }, (_, i) => (
                <button
                  key={i}
                  className="text-left px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-800 block whitespace-nowrap">
                    第 {comic.chapters - i} 话
                  </span>
                  <span className="text-xs text-foreground-400 mt-0.5 block">
                    {i === 0 ? '最新' : `${i + 1}天前`}
                  </span>
                </button>
              ))}
            </div>
            {comic.chapters > 20 && (
              <div className="text-center mt-8">
                <button className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 text-sm rounded-full hover:bg-background-50 transition-all cursor-pointer">
                  加载更多章节
                  <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center" />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8">
                同类型<span className="text-primary-500">推荐</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard
                    key={c.id}
                    id={c.id}
                    slug={c.slug}
                    title={c.title}
                    author={c.author}
                    category={c.category}
                    rating={c.rating}
                    cover={c.cover}
                    chapters={c.chapters}
                    views={c.views}
                    isHot={c.isHot}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}